import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Users, ShieldCheck, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { api, imgSrc } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader, PublicFooter, RecencyChip, Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OwnerEditButton, YourBusinessPill } from "@/components/OwnerEdit";
import { useBrowseLocation } from "@/hooks/useBrowseLocation";
import SetAreaBanner from "@/components/SetAreaBanner";
import BrowseServiceChips from "@/components/BrowseServiceChips";
import { browseLocationLine } from "@/lib/browseLocation";

const BranchGridCard = ({ b, onClick }) => {
  const photo = b.portfolio_image_urls?.[0];
  const locLine = browseLocationLine(b);
  return (
  <div className="relative">
  {b.is_owner && (
    <div className="absolute bottom-2 right-2 z-10">
      <OwnerEditButton branch={b} />
    </div>
  )}
  <motion.button
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={onClick}
    data-testid={`branch-card-${b.id}`}
    className="group flex w-full flex-col overflow-hidden rounded-[14px] border border-border bg-card text-left shadow-[0_8px_30px_rgba(27,122,94,0.06)] transition-transform duration-300 hover:-translate-y-1"
  >
    <div className="relative h-40 w-full overflow-hidden bg-gradient-to-br from-brand-green/20 to-brand-gold/20">
      {photo ? (
        <img src={imgSrc(photo)} alt={b.company_name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-5xl font-black text-muted-foreground/40" aria-hidden>
          {b.company_name?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      {b.subcategory_badge && (
        <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm" data-testid={`sub-badge-${b.id}`}>
          {b.subcategory_badge}
        </span>
      )}
      <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2.5 py-0.5 text-[13px] font-bold tracking-wide text-brand-gold backdrop-blur-sm" data-testid="payer-tier-badge" title="Payer tier — relative generosity">
        {b.payer_tier || "$"}
      </span>
    </div>
    <div className="flex flex-1 flex-col gap-2 p-5">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold tracking-tight">{b.company_name}</h3>
        <YourBusinessPill branch={b} />
      </div>
      {locLine ? (
        <p className="text-sm text-muted-foreground" data-testid={`browse-loc-${b.id}`}>{locLine}</p>
      ) : null}
      <BrowseServiceChips branch={b} />
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <RecencyChip recency={b.last_payout_recency} />
      </div>
      {b.reward_active && b.reward?.reward_type === "cash" && (
        <div className="mt-2 inline-flex w-fit items-center rounded-full bg-brand-gold/20 px-3 py-1 text-sm font-bold text-brand-gold-dark dark:text-brand-gold">
          Earn ${(b.reward.reward_amount_cents / 100).toFixed(0)} per referral
        </div>
      )}
    </div>
  </motion.button>
  </div>
  );
};

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [branches, setBranches] = useState(null);
  const [searched, setSearched] = useState(false);
  const {
    searchParams,
    needsAreaFallback,
    areaInput,
    setAreaInput,
    areaError,
    savingArea,
    setArea,
    coords,
  } = useBrowseLocation({ enabled: true });

  const search = async (query = "") => {
    const { data } = await api.get(`/branches/search`, {
      params: { q: query, ...searchParams },
    });
    setBranches(data);
    setSearched(!!query.trim());
  };
  useEffect(() => { search(q); }, [coords?.lat, coords?.lng]);

  const addMissing = () => {
    const name = q.trim();
    const dest = `/app/add-business?name=${encodeURIComponent(name)}`;
    if (!user) {
      nav(`/login?redirect=${encodeURIComponent(dest)}`);
      return;
    }
    nav(dest);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      <section className="bg-gradient-to-br from-brand-green via-brand-green-dark to-[#0e3f30] relative overflow-hidden text-white">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">Get rewarded for good word-of-mouth</p>
            <h1 className="text-4xl font-black leading-none tracking-tighter sm:text-5xl lg:text-6xl">
              Refer businesses you trust. <span className="text-brand-gold">Get paid</span> when they convert.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/85">
              ReferLinc turns your genuine recommendations into real rewards — and shows you which businesses are the most generous.
            </p>
            <div className="mt-8 flex max-w-lg items-center gap-2 rounded-[14px] bg-white p-2 shadow-lg">
              <Search className="ml-2 h-5 w-5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search(q)}
                placeholder="Search hair salons, restaurants, electricians…"
                data-testid="landing-search-input"
                className="flex-1 bg-transparent px-2 py-2 text-foreground outline-none"
              />
              <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => search(q)} data-testid="landing-search-btn">
                Search
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-white/80">
              <span className="inline-flex items-center gap-2"><Wallet className="h-4 w-4 text-brand-gold" /> Prepaid, guaranteed payouts</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-gold" /> Two-sided confirmation</span>
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-brand-gold" /> No install to claim</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Generous businesses near you</h2>
            <p className="mt-1 text-muted-foreground">Ranked by a blend of distance and generosity score.</p>
          </div>
          <Button variant="outline" onClick={() => nav("/login")} data-testid="cta-start-referring">
            Start referring <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <SetAreaBanner
          visible={needsAreaFallback}
          areaInput={areaInput}
          setAreaInput={setAreaInput}
          onSubmit={() => setArea().then((ok) => ok && search(q))}
          error={areaError}
          saving={savingArea}
        />
        {branches === null ? (
          <Loading />
        ) : searched && branches.length === 0 ? (
          <div className="rounded-[14px] border border-dashed border-border bg-card p-8 text-center" data-testid="landing-search-empty">
            <p className="text-muted-foreground">No businesses found.</p>
            <Button
              className="mt-4 bg-brand-green hover:bg-brand-green-dark"
              onClick={addMissing}
              data-testid="landing-add-missing-business-btn"
            >
              Can&apos;t find them? Add {q.trim()} to ReferLinc
            </Button>
          </div>
        ) : branches.length === 0 ? (
          <p className="text-muted-foreground">No businesses found.</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map((b) => (
              <BranchGridCard key={b.id} b={b} onClick={() => nav(`/b/${b.id}`)} />
            ))}
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
